import { LangObject } from "@brg/api/dist/api-core/utils/lang-utils";
import { FC, useCallback, useEffect, useState, useMemo } from "react";
import { ITeamFilterModel } from "./TeamFilterEditView";
import { ProjectEditPageGrid, ProjectEditPageGridRow } from "shared/pages/projectEdit/styles";
import { Lang,  Group, GroupAPI, Profile, API, Designer } from "@brg/api";
import { useTranslation } from "react-i18next";
import { CommonInput, FlexBox, INPUT_TYPES, useIsMobile, useToasts } from "@brg/ui";
import {cloneValue} from "../TeamEdit";
import {ModerationType} from "../const/ModerationTypes";
import { DefaultButton } from "../styles";
import { createDefaultLangObject } from "shared/components/__ADDONS__/__SAFEAPI__/Defaults";
import { InputTypes } from "../const/InputTypes";
import ProjectAuthorsEdit, { ProjectAuthorsChangeCbT } from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { createPortal } from "react-dom";
import { NavbarCreate } from "shared/components/navbar-elements";

export const inputTypes = (): { label: string, value: number }[] => {
    //@ts-ignore
    return Object.keys(InputTypes)
    ?.map((type, idx) => ({ label: type.toLowerCase(), value: idx }))
    ?.filter((type) => ['text', 'phone', 'name', 'surname', 'mail'].includes(type.label))
    ?.sort((a,b) => b.label === 'name' ? 1 : b.label === 'surname' ? 0 : -1)
}

const getTypeName = (type: number): string => inputTypes()?.find((item) => item.value === type)?.label || '';

const setTextByLang = (
    current: LangObject[],
    lang: string,
    value: string
  ): LangObject[] => {
    const engValue = current.find(({ lang }) => lang === 'en') ?? {
        lang: 'en',
        name: '',
    };
    const ruValue = current.find(({ lang }) => lang === 'ru') ?? {
        lang: 'ru',
        name: '',
    };

    if (lang === 'ru') {
        return [{
            lang: 'ru',
            name: value,
        }, engValue];
    } else {
        return [ruValue, {
            lang: 'en',
            name: value,
        }];
    }
}

export interface ITeamTab {
    path: string;
    name: LangObject[];
    about: LangObject[];
    filter: ITeamFilterModel;
    isMain: boolean;
}

type TeamFormsEditPropsT = {
    group: Group<'Global'>;
    actualTeamMembers?: Designer<'Local'>[];
    actualTeamOwners?: Designer<'Local'>[];
    mobileEditorOpen?: boolean;
    channelId?: string;
    onUpdate: (val: any) => void;
    onDelete: (val: string) => void;
    onStatusSet: (val: string) => void;
    onBackClick: () => void;
    onResetGroup?: () => void;
    teamId?: string;
    status?: string;
    channelType?: ModerationType
}

const NEW_FIELD = { type: 0, header: createDefaultLangObject(''), placeholder: createDefaultLangObject(''), isRequired: true }

export const TeamGroupEdit: FC<TeamFormsEditPropsT> = (props) => {
    const { t, i18n } = useTranslation();
    const isMobile = useIsMobile();
    const toasts = useToasts();   
    const [techName, setTechName] = useState<LangObject[]>([]);
    const [statusText, setStatusText] = useState('');
    const [members, setMembers] = useState<Profile<'Global'>[]>([]);
    const [teachers, setTeachers] = useState<Profile<'Global'>[]>([]);
    const [chatLink, setChatLink] = useState<string | undefined>();
    
    const { 
        group, 
        onUpdate, 
        onStatusSet, 
        onBackClick, 
        onDelete,
        onResetGroup,
        mobileEditorOpen, 
        actualTeamMembers, 
        actualTeamOwners,
    } = props;

    const filteredAuthors = group.members?.filter(author =>
        actualTeamMembers?.some(val => val.accountId === author.accountId)
      );

      //@ts-ignore
      const filteredTeachers = group.teachers?.filter(author =>
        actualTeamOwners?.some(val => val.accountId === author.accountId)
    );
    
    const onMembersChange = ({ authors }: ProjectAuthorsChangeCbT) => {
        //@ts-ignore
        setMembers(authors);
    };

    const onTeachersChange = ({ authors }: ProjectAuthorsChangeCbT) => {
        //@ts-ignore
        setTeachers(authors);
    };

    const onGoToBack = () => {
        onBackClick?.()
        onResetGroup?.()
    }

    const isSelect = (id: string) => members.map(x => x.designerId)?.includes(id);

    useEffect(() => {
        if (group) {
            setTechName(group.name)
            //@ts-ignore
            setMembers(API.toLang(filteredAuthors, i18n.language))
            //@ts-ignore
            setTeachers(API.toLang(filteredTeachers || [], i18n.language))
            //@ts-ignore
            setChatLink(group.chatLink);
        }
    } , [group])

    const onCreateForm = useCallback(() => {
        if (group) {
            onStatusSet(t('saving'));
            //@ts-ignore
            const copy: Group<'Global'> = { ...group};
            //@ts-ignore
            copy.name = cloneValue(techName);
            //@ts-ignore
            copy.membersIDs = members.map(x => x.designerId);
            //@ts-ignore
            copy.teachersIDs = teachers.map(x => x.designerId);
            //@ts-ignore
            copy.chatLink = chatLink;

            GroupAPI.Update(copy).then((r) => {
                onStatusSet(t('editing_saved'))
                toasts.info(t('teams.admin.msg.save'));
                onResetGroup?.();
                onBackClick?.();
                onUpdate(Date.now())
            })

        } else {
            onStatusSet('Loading team...');
        }
    } , [group, techName, members, chatLink, teachers])

    const primaryButtonEnabled = useMemo(() => {
        return techName?.some(({name}) => !!name)
    } , [techName])

    const errors = useMemo(() => {
        const errors = [];

        if (!primaryButtonEnabled) {
            errors.push(`${t('teams.adminPage.errors.group_name')}`);
        }

            return errors;
    }, [primaryButtonEnabled])

     const PortalMenu = useMemo(() => {
            const outer = document.getElementById('navbar-portal');
    
            if (!outer) {
                return null;
            }
    
            return createPortal(
                <NavbarCreate
                    previewButtonVisible={false}
                    primaryButtonEnabled={primaryButtonEnabled}
                    primaryButtonText={t('save')}
                    secondaryButtonText={t("nav.exit_other")}
                    mobileEditorOpen={mobileEditorOpen}
                    errors={errors}
                    // toggleMobileEditor={setMobileEditorOpen}
                    stateText={statusText}
                    onSecondaryClick={onGoToBack}
                    onPrimaryClick={onCreateForm}
                    // showStatusPfx={statusPfx}
                />,
                outer,
            );
        }, [primaryButtonEnabled, onBackClick, onCreateForm, statusText]);

    return (
        <>
        {/*<PageViewportLayout offsetTop={40}>*/}
            {PortalMenu}
            <ProjectEditPageGrid isTeamPage offsetBottom={40}>

                {/* <ProjectEditPageGridRow isTeamEdit>
                    {!isS && (
                        <ProjectEditPageGridLabel isTeamEdit>
                            {''}
                        </ProjectEditPageGridLabel>
                    )}
                    <FlexBox width={'100%'}  widthMobile={isS ? '100%' : '558px'} style={{ maxWidth: isS ? '100%' : '600px' }} >
                        <BLanguageSelectionComponent
                            isProjectPage={true}
                            onLangSelect={(lang) => {
                                SaveUserLang(lang);
                                setAppLang(lang);
                                i18n.changeLanguage(lang);
                            }}
                            key={'lang'}
                            fs={true}
                        />
                    </FlexBox>
                </ProjectEditPageGridRow> */}

                <ProjectEditPageGridRow style={{marginTop: isMobile? 24 : 0, maxWidth: 600 }} isTeamEdit>
                    <CommonInput 
                        type={INPUT_TYPES.counter}
                        orientation="wide"
                        label={t('teams.adminPage.fields.group_name')}
                        maxLength={50}
                        value={Lang(techName, i18n.language)}
                        onChange={(ev: any) => {
                            setTechName(setTextByLang(techName, i18n.language, ev.target.value))
                        }}
                    />     
                </ProjectEditPageGridRow>

                <FlexBox style={{marginTop: isMobile ? 8 : 16}}>
                    <ProjectAuthorsEdit
                        label={t('teams.adminPage.fields.members')}
                        //@ts-ignore
                        value={members}
                        onChange={onMembersChange}
                        onSelect={isSelect}
                        placeholder={t('search_by_name')}
                        orientation="wide"
                        isTeamEdit
                        isAdminPage
    // @ts-ignore       
                        isLMS={true}
                        teamId={props?.teamId}
                    />
                </FlexBox>

                <ProjectEditPageGridRow style={{marginTop: isMobile? 8 : 16, maxWidth: 600 }} isTeamEdit>
                    <CommonInput 
                        type={INPUT_TYPES.counter}
                        orientation="wide"
                        label={t('teams.adminPage.fields.group_chatlink')}
                        maxLength={50}
                        value={chatLink}
                        onChange={(ev: any) => {
                            setChatLink(ev.target.value)
                        }}
                    />
                </ProjectEditPageGridRow>

                <FlexBox style={{marginTop: isMobile ? 8 : 16}}>
                    <ProjectAuthorsEdit
                        label={t('teams.adminPage.fields.teachers')}
                        //@ts-ignore
                        value={teachers}
                        onChange={onTeachersChange}
                        onSelect={isSelect}
                        orientation="wide"
                        placeholder={t('search_by_name')}
                        isTeamEdit
                        isAdminPage
    // @ts-ignore
                        isLMS={true}
                        isTeacherLMS
                        teamId={props?.teamId}
                    />
                </FlexBox>

                {/* {createAutoChannelBlock} */}
                <DefaultButton style={{ marginTop: 24 }} wh={131} whMob={107} onClick={() => onDelete(group?.id)}>{t('teams.adminPage.buttons.delete')}</DefaultButton>

            </ProjectEditPageGrid>
     
        {/*</PageViewportLayout>*/}
        </>
    );
};
import { LangObject } from "@brg/api/dist/api-core/utils/lang-utils";
import { FC, useCallback, useEffect, useState, useMemo } from "react";
import { ITeamFilterModel } from "./TeamFilterEditView";
import { ProjectEditPageGrid, ProjectEditPageGridRow } from "shared/pages/projectEdit/styles";
import { Lang,  Group, GroupAPI, Profile, API, Designer } from "@brg/api";
import { useTranslation } from "react-i18next";
import { CommonInput, FlexBox, INPUT_TYPES, useIsMobile, useToasts } from "@brg/ui";
import {cloneValue} from "../TeamEdit";
import {ModerationType} from "../const/ModerationTypes";
import { DefaultButton } from "../styles";
import { createDefaultLangObject } from "shared/components/__ADDONS__/__SAFEAPI__/Defaults";
import { InputTypes } from "../const/InputTypes";
import ProjectAuthorsEdit, { ProjectAuthorsChangeCbT } from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { createPortal } from "react-dom";
import { NavbarCreate } from "shared/components/navbar-elements";

export const inputTypes = (): { label: string, value: number }[] => {
    //@ts-ignore
    return Object.keys(InputTypes)
    ?.map((type, idx) => ({ label: type.toLowerCase(), value: idx }))
    ?.filter((type) => ['text', 'phone', 'name', 'surname', 'mail'].includes(type.label))
    ?.sort((a,b) => b.label === 'name' ? 1 : b.label === 'surname' ? 0 : -1)
}

const getTypeName = (type: number): string => inputTypes()?.find((item) => item.value === type)?.label || '';

const setTextByLang = (
    current: LangObject[],
    lang: string,
    value: string
  ): LangObject[] => {
    const engValue = current.find(({ lang }) => lang === 'en') ?? {
        lang: 'en',
        name: '',
    };
    const ruValue = current.find(({ lang }) => lang === 'ru') ?? {
        lang: 'ru',
        name: '',
    };

    if (lang === 'ru') {
        return [{
            lang: 'ru',
            name: value,
        }, engValue];
    } else {
        return [ruValue, {
            lang: 'en',
            name: value,
        }];
    }
}

export interface ITeamTab {
    path: string;
    name: LangObject[];
    about: LangObject[];
    filter: ITeamFilterModel;
    isMain: boolean;
}

type TeamFormsEditPropsT = {
    group: Group<'Global'>;
    actualTeamMembers?: Designer<'Local'>[];
    actualTeamOwners?: Designer<'Local'>[];
    mobileEditorOpen?: boolean;
    channelId?: string;
    onUpdate: (val: any) => void;
    onDelete: (val: string) => void;
    onStatusSet: (val: string) => void;
    onBackClick: () => void;
    onResetGroup?: () => void;
    teamId?: string;
    status?: string;
    channelType?: ModerationType
}

const NEW_FIELD = { type: 0, header: createDefaultLangObject(''), placeholder: createDefaultLangObject(''), isRequired: true }

export const TeamGroupEdit: FC<TeamFormsEditPropsT> = (props) => {
    const { t, i18n } = useTranslation();
    const isMobile = useIsMobile();
    const toasts = useToasts();   
    const [techName, setTechName] = useState<LangObject[]>([]);
    const [statusText, setStatusText] = useState('');
    const [members, setMembers] = useState<Profile<'Global'>[]>([]);
    const [teachers, setTeachers] = useState<Profile<'Global'>[]>([]);
    const [chatLink, setChatLink] = useState<string | undefined>();
    
    const { 
        group, 
        onUpdate, 
        onStatusSet, 
        onBackClick, 
        onDelete,
        onResetGroup,
        mobileEditorOpen, 
        actualTeamMembers, 
        actualTeamOwners,
    } = props;

    const filteredAuthors = group.members?.filter(author =>
        actualTeamMembers?.some(val => val.accountId === author.accountId)
      );

      //@ts-ignore
      const filteredTeachers = group.teachers?.filter(author =>
        actualTeamOwners?.some(val => val.accountId === author.accountId)
    );
    
    const onMembersChange = ({ authors }: ProjectAuthorsChangeCbT) => {
        //@ts-ignore
        setMembers(authors);
    };

    const onTeachersChange = ({ authors }: ProjectAuthorsChangeCbT) => {
        //@ts-ignore
        setTeachers(authors);
    };

    const onGoToBack = () => {
        onBackClick?.()
        onResetGroup?.()
    }

    const isSelect = (id: string) => members.map(x => x.designerId)?.includes(id);

    useEffect(() => {
        if (group) {
            setTechName(group.name)
            //@ts-ignore
            setMembers(API.toLang(filteredAuthors, i18n.language))
            //@ts-ignore
            setTeachers(API.toLang(filteredTeachers || [], i18n.language))
            //@ts-ignore
            setChatLink(group.chatLink);
        }
    } , [group])

    const onCreateForm = useCallback(() => {
        if (group) {
            onStatusSet(t('saving'));
            //@ts-ignore
            const copy: Group<'Global'> = { ...group};
            //@ts-ignore
            copy.name = cloneValue(techName);
            //@ts-ignore
            copy.membersIDs = members.map(x => x.designerId);
            //@ts-ignore
            copy.teachersIDs = teachers.map(x => x.designerId);
            //@ts-ignore
            copy.chatLink = chatLink;

            GroupAPI.Update(copy).then((r) => {
                onStatusSet(t('editing_saved'))
                toasts.info(t('teams.admin.msg.save'));
                onResetGroup?.();
                onBackClick?.();
                onUpdate(Date.now())
            })

        } else {
            onStatusSet('Loading team...');
        }
    } , [group, techName, members, chatLink, teachers])

    const primaryButtonEnabled = useMemo(() => {
        return techName?.some(({name}) => !!name)
    } , [techName])

    const errors = useMemo(() => {
        const errors = [];

        if (!primaryButtonEnabled) {
            errors.push(`${t('teams.adminPage.errors.group_name')}`);
        }

            return errors;
    }, [primaryButtonEnabled])

     const PortalMenu = useMemo(() => {
            const outer = document.getElementById('navbar-portal');
    
            if (!outer) {
                return null;
            }
    
            return createPortal(
                <NavbarCreate
                    previewButtonVisible={false}
                    primaryButtonEnabled={primaryButtonEnabled}
                    primaryButtonText={t('save')}
                    secondaryButtonText={t("nav.exit_other")}
                    mobileEditorOpen={mobileEditorOpen}
                    errors={errors}
                    // toggleMobileEditor={setMobileEditorOpen}
                    stateText={statusText}
                    onSecondaryClick={onGoToBack}
                    onPrimaryClick={onCreateForm}
                    // showStatusPfx={statusPfx}
                />,
                outer,
            );
        }, [primaryButtonEnabled, onBackClick, onCreateForm, statusText]);

    return (
        <>
        {/*<PageViewportLayout offsetTop={40}>*/}
            {PortalMenu}
            <ProjectEditPageGrid isTeamPage offsetBottom={40}>

                {/* <ProjectEditPageGridRow isTeamEdit>
                    {!isS && (
                        <ProjectEditPageGridLabel isTeamEdit>
                            {''}
                        </ProjectEditPageGridLabel>
                    )}
                    <FlexBox width={'100%'}  widthMobile={isS ? '100%' : '558px'} style={{ maxWidth: isS ? '100%' : '600px' }} >
                        <BLanguageSelectionComponent
                            isProjectPage={true}
                            onLangSelect={(lang) => {
                                SaveUserLang(lang);
                                setAppLang(lang);
                                i18n.changeLanguage(lang);
                            }}
                            key={'lang'}
                            fs={true}
                        />
                    </FlexBox>
                </ProjectEditPageGridRow> */}

                <ProjectEditPageGridRow style={{marginTop: isMobile? 24 : 0, maxWidth: 600 }} isTeamEdit>
                    <CommonInput 
                        type={INPUT_TYPES.counter}
                        orientation="wide"
                        label={t('teams.adminPage.fields.group_name')}
                        maxLength={50}
                        value={Lang(techName, i18n.language)}
                        onChange={(ev: any) => {
                            setTechName(setTextByLang(techName, i18n.language, ev.target.value))
                        }}
                    />     
                </ProjectEditPageGridRow>

                <FlexBox style={{marginTop: isMobile ? 8 : 16}}>
                    <ProjectAuthorsEdit
                        label={t('teams.adminPage.fields.members')}
                        //@ts-ignore
                        value={members}
                        onChange={onMembersChange}
                        onSelect={isSelect}
                        placeholder={t('search_by_name')}
                        orientation="wide"
                        isTeamEdit
                        isAdminPage
    // @ts-ignore       
                        isLMS={true}
                        teamId={props?.teamId}
                    />
                </FlexBox>

                <ProjectEditPageGridRow style={{marginTop: isMobile? 8 : 16, maxWidth: 600 }} isTeamEdit>
                    <CommonInput 
                        type={INPUT_TYPES.counter}
                        orientation="wide"
                        label={t('teams.adminPage.fields.group_chatlink')}
                        maxLength={50}
                        value={chatLink}
                        onChange={(ev: any) => {
                            setChatLink(ev.target.value)
                        }}
                    />
                </ProjectEditPageGridRow>

                <FlexBox style={{marginTop: isMobile ? 8 : 16}}>
                    <ProjectAuthorsEdit
                        label={t('teams.adminPage.fields.teachers')}
                        //@ts-ignore
                        value={teachers}
                        onChange={onTeachersChange}
                        onSelect={isSelect}
                        orientation="wide"
                        placeholder={t('search_by_name')}
                        isTeamEdit
                        isAdminPage
    // @ts-ignore
                        isLMS={true}
                        isTeacherLMS
                        teamId={props?.teamId}
                    />
                </FlexBox>

                {/* {createAutoChannelBlock} */}
                <DefaultButton style={{ marginTop: 24 }} wh={131} whMob={107} onClick={() => onDelete(group?.id)}>{t('teams.adminPage.buttons.delete')}</DefaultButton>

            </ProjectEditPageGrid>
     
        {/*</PageViewportLayout>*/}
        </>
    );
};
